import React, {Fragment, useEffect, useMemo} from "react";
import {Box} from "@mui/material";
import Helmet from "react-helmet";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {HashLink} from "react-router-hash-link";
import DatasetHandler from "../components/dataset-handler";
import Footer from "../components/footer";
import Header from "../components/header";
import ModulesPlaceholder from "../components/modules-placeholder";
import PackageViewer from "../components/package-viewer";
import Page from "../components/page";
import ResourceHandler from "../components/resource-handler";
import {getNotFoundPageInternalUrl} from "../constants/appRoutes";
import {isDataportalNode} from "../constants/nodes";
import {USER_ERRORS_NOT_ACCESSIBLE_DATASET} from "../translations/getManagedErrorsTranslations";
import {appConfigSelector} from "../state/app-config/appConfigSelectors";
import {packageLoaded, packageUnloaded} from "../state/miscellaneous/miscellaneousActions";
import {showUserLoginForm} from "../state/user/userActions";
import {userSelector} from "../state/user/userSelectors";
import {
  CATALOG_RESOURCE_TYPE_CSV,
  CATALOG_RESOURCE_TYPE_DATASET,
  CATALOG_RESOURCE_TYPE_GEOJSON,
  CATALOG_RESOURCE_TYPE_METADATA_URL,
  CATALOG_RESOURCE_TYPE_ODATA,
  CATALOG_RESOURCE_TYPE_WMS,
  CATALOG_RESOURCE_TYPE_WMTS,
  getDatasetFromCatalog,
  getPackageExternalResources,
  getResourceFromCatalogById,
  getResourceFromCatalogByType
} from "../utils/catalog";
import EXTRAS from "../utils/extras";
import {getPageTitle} from "../utils/other";
import {getIsUserAuthenticated} from "../utils/permissions";

const getAppConfigResourceType = (appConfig, nodeCode, packageId) => {
  const nodeMainResourceMap = appConfig?.mainResourceMap?.[nodeCode];
  if (!nodeMainResourceMap) return null;

  if (nodeMainResourceMap.overrides && nodeMainResourceMap.overrides[packageId]) {
    return nodeMainResourceMap.overrides[packageId];
  }

  return nodeMainResourceMap.defaultType || null;
};

const getSelectedResource = (catalog, packageId, resourceId, resourceType) => {
  const catalogPackage = packageId ? catalog.packageMap[packageId] : null;

  const orderedResourceTypes = [
    {
      type: CATALOG_RESOURCE_TYPE_ODATA,
      condition: resource => resource.aggregable !== false
    },
    {
      type: CATALOG_RESOURCE_TYPE_DATASET
    },
    {
      type: CATALOG_RESOURCE_TYPE_ODATA
    },
    {
      type: CATALOG_RESOURCE_TYPE_CSV
    },
    {
      type: CATALOG_RESOURCE_TYPE_GEOJSON
    },
    {
      type: CATALOG_RESOURCE_TYPE_WMS
    },
    {
      type: CATALOG_RESOURCE_TYPE_WMTS
    }
  ];

  if (resourceType) {
    if (typeof resourceType === "string") {
      orderedResourceTypes.unshift({type: resourceType});
    } else if (Array.isArray(resourceType)) {
      orderedResourceTypes.unshift(...resourceType.map(type => ({type: type})));
    }
  }

  if (resourceId && (catalogPackage?.dataflowIdentifiers || []).includes(resourceId)) {
    const dataset = getDatasetFromCatalog(packageId, catalog);
    if (dataset) {
      return dataset;
    }
  }
  if (resourceId && (catalogPackage?.resourceIdentifiers || []).includes(resourceId)) {
    const resource = getResourceFromCatalogById(catalog, resourceId);
    if (resource) {
      return resource;
    }
  }
  for (const {type, condition} of orderedResourceTypes) {
    if (!type) continue;
    const resource = getResourceFromCatalogByType(catalog, packageId, type);
    if (!resource) continue;
    if (!condition || condition(resource)) {
      return resource;
    }
  }
  return null;
};

const ViewersDomain = ({hub, node, nodeCode, isDefault, catalog, categoryPath, packageId: externalPackageId}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {packageId: urlPackageId} = useParams();
  const [searchParams] = useSearchParams();

  const appConfig = useSelector(appConfigSelector);

  const nodeExtras = useMemo(() => EXTRAS.parse(node?.extras), [node]);

  const packageId = externalPackageId ? externalPackageId : urlPackageId;

  const catalogPackage = useMemo(() => catalog?.packageMap?.[packageId] || null, [catalog, packageId]);
  const packageTitle = useMemo(() => catalogPackage?.title ?? "", [catalogPackage]);
  const packageAttachments = useMemo(() => getPackageExternalResources(packageId, catalog), [catalog, packageId]);
  const packageMetadata = useMemo(
    () =>
      isDataportalNode(node) && catalog?.packageMap?.[packageId]
        ? true
        : getResourceFromCatalogByType(catalog, packageId, CATALOG_RESOURCE_TYPE_METADATA_URL),
    [packageId, node, catalog]
  );

  const urlResourceId = useMemo(() => searchParams.get("resourceId") || null, [searchParams]);

  const urlResourceType = useMemo(() => searchParams.get("type") || null, [searchParams]);

  const appConfigResourceType = useMemo(
    () => getAppConfigResourceType(appConfig, nodeCode, packageId),
    [appConfig, nodeCode, packageId]
  );

  const selectedResource = useMemo(
    () => getSelectedResource(catalog, packageId, urlResourceId, urlResourceType || appConfigResourceType),
    [catalog, packageId, urlResourceId, urlResourceType, appConfigResourceType]
  );

  const user = useSelector(userSelector);
  useEffect(() => {
    if (!catalogPackage) {
      if (!getIsUserAuthenticated(user)) {
        if (!user.isLoginDialogOpen) {
          dispatch(showUserLoginForm(USER_ERRORS_NOT_ACCESSIBLE_DATASET, "warning", true));
        }
      } else {
        navigate(getNotFoundPageInternalUrl());
      }
    }
  }, [catalogPackage, user, dispatch, navigate]);

  useEffect(() => {
    if (packageId) {
      dispatch(packageLoaded(packageId));
    }
    return () => dispatch(packageUnloaded(packageId));
  }, [packageId, dispatch]);

  const viewId = useMemo(() => searchParams.get("view") || undefined, [searchParams]);

  const isAccessible = useMemo(() => !!searchParams.get("accessible"), [searchParams]);

  const accessiblePath = useMemo(() => {
    const accessibleParams = new URLSearchParams(searchParams);
    accessibleParams.set("accessible", "true");
    accessibleParams.set("datasetId", packageId);

    return (
      `/${window.language}/${node?.code.toLowerCase()}/${categoryPath.join("/")}` +
      (accessibleParams.size > 0 ? "?" : "") +
      accessibleParams.toString()
    );
  }, [searchParams, categoryPath, node, packageId]);

  return (
    <Fragment>
      <Helmet>
        <body data-dataset-id={`${packageId.replaceAll(/[,.]/g, "-")}`} />
      </Helmet>
      {!isAccessible && (
        <a href={accessiblePath} target="_self" className="skip-link sr-only">
          {t("commons.hashLinks.accessible")}
        </a>
      )}
      <HashLink to={{hash: "#main", search: searchParams.toString()}} className="skip-link sr-only">
        {t("commons.hashLinks.main")}
      </HashLink>
      <HashLink to={{hash: "#footer", search: searchParams.toString()}} className="skip-link sr-only">
        {t("commons.hashLinks.footer")}
      </HashLink>
      <Page title={getPageTitle([packageTitle], t)}>
        <Header isDefault={isDefault} selectedCategoryPath={categoryPath} selectedDataset={packageId} />
        <Box
          key={packageId}
          id="main"
          component="main"
          sx={{
            backgroundColor: "#f5f5f5",
            width: "100%",
            height: "100%",
            position: "fixed",
            display: "flex",
            flexDirection: "column",
            "& #footer": {
              paddingTop: 0
            }
          }}
          className={"data"}
        >
          {catalogPackage ? (
            selectedResource && selectedResource?.type ? (
              selectedResource.type === CATALOG_RESOURCE_TYPE_DATASET ? (
                <DatasetHandler
                  hub={hub}
                  node={node}
                  nodeCode={nodeCode}
                  nodeExtras={nodeExtras}
                  catalog={catalog}
                  categoryPath={categoryPath}
                  packageId={packageId}
                  packageTitle={packageTitle}
                  packageMetadata={packageMetadata}
                  packageApproved={catalogPackage.public}
                  viewId={viewId}
                  isAccessible={isAccessible}
                  packageAttachments={packageAttachments}
                />
              ) : (
                <ResourceHandler
                  categoryPath={categoryPath}
                  packageId={packageId}
                  packageTitle={packageTitle}
                  packageMetadata={packageMetadata}
                  packageAttachments={packageAttachments}
                  packageApproved={catalogPackage.public}
                  resource={selectedResource}
                  viewId={viewId}
                />
              )
            ) : (
              <PackageViewer
                categoryPath={categoryPath}
                packageId={packageId}
                packageTitle={packageTitle}
                packageMetadata={packageMetadata}
                packageAttachments={packageAttachments}
                packageApproved={catalogPackage.public}
              />
            )
          ) : null}
          <Footer />
          <ModulesPlaceholder id="data-main-page" />
        </Box>
      </Page>
    </Fragment>
  );
};

export default ViewersDomain;
